import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context/AuthContext";
import { Input } from "antd";
import { SvgArrowUp } from "ui/svgs";
import { formatContextDate, colorHash } from "lib/utils";
import { findCaseNotes, addCaseNote } from "pages/dashboard/cases/service";

export default function Notes({
  caseId,
  maxHeight,
}: {
  caseId: string;
  maxHeight: number;
}) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!caseId) {
      return;
    }
    setNotes([]);
    fetchNotes();
  }, [caseId]);

  const fetchNotes = async () => {
    try {
      setIsAdding(true);
      const res = await findCaseNotes(user.orgId, caseId);
      if (res.status !== 200) {
        setIsAdding(false);
        setNotes([]);
        return;
      }
      console.log("Fetched notes response:", res?.data);
      setNotes(Array.isArray(res?.data?.notes) ? res?.data?.notes : []);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
      setNotes([]);
    }
    setIsAdding(false);
  };

  const handleAddNote = async () => {
    try {
      await addCaseNote(user.orgId, caseId, newNote);
      setNewNote("");
      fetchNotes();
    } catch (error) {
      console.error("Failed to add note:", error);
    }
  };

  const getUserInitials = (name) => {
    console.log("getUserInitials:", name);
    return name?.at(0)?.toUpperCase();
  };

  return (
    <div className="flex flex-col px-4 py-1" style={{ maxHeight: maxHeight }}>
      <div className="mt-4 flex gap-2 items-center">
        <div
          className="  text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: colorHash.hex(user.name || "") }}
        >
          {getUserInitials(user.name || "")}
        </div>
        <Input
          disabled={isAdding}
          placeholder="Enter your note"
          className="rounded-xl"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          suffix={
            <button
              className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-400 hover:bg-blue-300 fill-white  disabled:bg-neutral-300 disabled:fill-neutral-500  disabled:hover:bg-neutral-300 disabled:cursor-default"
              disabled={isAdding || newNote.trim() === ""}
              onClick={handleAddNote}
            >
              <SvgArrowUp width="16" className="" />
            </button>
          }
        />
      </div>
      <div className="mt-4 flex-grow overflow-y-auto">
        {isAdding && (
          <div
            className="ml-1 text-blue-500  inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
        )}
        {!isAdding && notes.length === 0 ? (
          <p></p>
        ) : (
          [...notes].reverse().map((note) => (
            <div key={note.noteId} className="flex flex-col py-2 mb-1">
              <div className="flex items-center mb-2">
                <div
                  className="w-8 h-8  text-white flex items-center justify-center rounded-full"
                  style={{ backgroundColor: colorHash.hex(note.user.name) }}
                >
                  {getUserInitials(note.user.name)}
                </div>
                <div className="ml-2">
                  <p className="text-sm font-semibold">{note.user.name}</p>
                  <p className="text-xs text-gray-500">
                    {formatContextDate(note.createdAt * 1000)}
                  </p>
                </div>
              </div>
              <p className="text-sm">{note.note}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
